import React, { useState, useEffect } from 'react';
import './RefreshPanel.css';
import CacheImportSection from './CacheImportSection';
import { formatApiDateTime } from '../utils/dateTime';

const ALL_DATA_TYPES = [
  { id: 'referrals', name: 'Referrals' },
  { id: 'vitalrounds', name: 'VitalRounds' },
  { id: 'messages', name: 'Messages' },
  // 'groups' (rpt_consolidated_group_usage) retired 2026-05 — see
  // backend/Services/DataCacheService.cs DataTypeNames for rationale
  // and a one-shot SQL cleanup for orphaned cache rows.
  { id: 'logins', name: 'Logins' },
  { id: 'studies', name: 'Studies' },
  // V2.1 endpoints (added 2026-05). Order kept after the original six so
  // the existing checkbox layout doesn't reflow for users who muscle-
  // memory the columns. Keep this list in lock-step with
  // backend/Services/DataCacheService.cs DataTypeNames.
  { id: 'group-referrals', name: 'Group Referrals' },
  { id: 'faxes', name: 'Faxes' },
  { id: 'fax-recipients', name: 'Fax Recipients' },
  { id: 'study-details', name: 'Study Details' },
  { id: 'referral-events', name: 'Referral Schedule Events' }
];

function RefreshPanel({
  cacheStatus,
  onRefresh,
  onRefreshFacilities,
  onCancelRefresh,
  onOpenPhiConfig,
  onImportSnapshot,
  dataRefreshing,
  facilitiesRefreshing,
  refreshProgress,
  defaultExpanded = false
}) {
  const [selectedTypes, setSelectedTypes] = useState(new Set(ALL_DATA_TYPES.map(dt => dt.id)));
  const [dateRange, setDateRange] = useState('last_month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [mode, setMode] = useState('smart');
  const [wipeConfirmed, setWipeConfirmed] = useState(false);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [autoExpanded, setAutoExpanded] = useState(defaultExpanded);

  useEffect(() => {
    const { start, end } = calculateDateRange('last_month');
    setStartDate(formatDate(start));
    setEndDate(formatDate(end));
  }, []);

  useEffect(() => {
    if (!autoExpanded && cacheStatus) {
      const dataStatuses = cacheStatus?.dataTypes || [];
      const hasData = dataStatuses.some(s => s.recordCount > 0);
      if (!hasData) {
        setExpanded(true);
      }
      setAutoExpanded(true);
    }
  }, [cacheStatus, autoExpanded]);

  useEffect(() => {
    if (dateRange !== 'custom') {
      const { start, end } = calculateDateRange(dateRange);
      setStartDate(formatDate(start));
      setEndDate(formatDate(end));
    }
  }, [dateRange]);

  const calculateDateRange = (preset) => {
    const end = new Date();
    const start = new Date();
    switch (preset) {
      case 'last_week': start.setDate(start.getDate() - 7); break;
      case 'last_month': start.setDate(start.getDate() - 30); break;
      case 'last_3_months': start.setMonth(start.getMonth() - 3); break;
      case 'last_6_months': start.setMonth(start.getMonth() - 6); break;
      case 'last_year': start.setFullYear(start.getFullYear() - 1); break;
      case 'last_18_months': start.setMonth(start.getMonth() - 18); break;
      case 'last_2_years': start.setFullYear(start.getFullYear() - 2); break;
      default: break;
    }
    return { start, end };
  };

  const formatDate = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  // 'YYYY-MM-DD' -> 'M/D/YYYY' for the inline date-range hint.
  const formatDateShort = (yyyymmdd) => {
    if (!yyyymmdd) return '';
    const [y, m, d] = yyyymmdd.split('-');
    if (!y || !m || !d) return yyyymmdd;
    return `${parseInt(m, 10)}/${parseInt(d, 10)}/${y}`;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'Never';
    return formatApiDateTime(dateStr);
  };

  const truncate = (s, max = 120) => {
    if (!s || typeof s !== 'string') return '';
    const t = s.trim();
    return t.length <= max ? t : `${t.slice(0, max)}…`;
  };

  /** @param {string} isoOrYmd ISO string or yyyy-mm-dd */
  const formatRefreshRangePiece = (isoOrYmd) => {
    if (!isoOrYmd) return '—';
    const day = isoOrYmd.includes('T') ? isoOrYmd.split('T')[0] : isoOrYmd;
    return formatDateShort(day);
  };

  const toggleType = (typeId) => {
    setSelectedTypes(prev => {
      const next = new Set(prev);
      if (next.has(typeId)) next.delete(typeId);
      else next.add(typeId);
      return next;
    });
  };

  const selectAllTypes = () => setSelectedTypes(new Set(ALL_DATA_TYPES.map(dt => dt.id)));
  const selectNoneTypes = () => setSelectedTypes(new Set());

  const handleRefresh = () => {
    if (selectedTypes.size === 0 || !startDate || !endDate) return;
    if (mode === 'full-wipe' && !wipeConfirmed) return;
    onRefresh(Array.from(selectedTypes), startDate, endDate, mode);
  };

  const handleModeChange = (next) => {
    setMode(next);
    if (next !== 'full-wipe') setWipeConfirmed(false);
  };

  const modeHelp = {
    smart: 'Keeps existing rows and inserts records that are not already cached.',
    'full-range': 'Replaces cached rows for the selected types only inside this date range. Older and newer rows stay.',
    'full-wipe': 'Deletes all cached rows for the selected types, then loads only this date range. History outside the range is lost.',
  }[mode];

  const dataStatuses = cacheStatus?.dataTypes || [];
  const facilityStatus = cacheStatus?.facilities || {};
  const salesforceSyncEnabled = cacheStatus?.salesforceSyncEnabled !== false;
  const hasAnyCachedData = dataStatuses.some(s => s.recordCount > 0) || facilityStatus.facilityCount > 0;
  const scheduledRefresh = !!refreshProgress?.scheduledMode;
  const typeOrder = ALL_DATA_TYPES.map(t => t.id);

  return (
    <div className="refresh-panel">
      <div className="refresh-panel-header" onClick={() => setExpanded(!expanded)}>
        <div className="refresh-panel-title">
          <h3>Data Management</h3>
          {!expanded && (
            <span className={`cache-summary ${hasAnyCachedData ? '' : 'cache-empty'}`}>
              {hasAnyCachedData
                ? `${dataStatuses.filter(s => s.recordCount > 0).length} of ${dataStatuses.length} data types cached${facilityStatus.facilityCount > 0 ? ` | ${facilityStatus.facilityCount} facilities` : ''}`
                : 'No data cached — expand to refresh from VitalEngine'}
            </span>
          )}
        </div>
        <button className="expand-toggle">{expanded ? '▲' : '▼'}</button>
      </div>

      {expanded && (
        <div className="refresh-panel-body">
          <div className="status-cards">
            {dataStatuses.map(status => {
              const rb = refreshProgress?.resultsByType?.[status.dataType];
              const typeIdx = typeOrder.indexOf(status.dataType);
              const running =
                (dataRefreshing && refreshProgress?.currentType === status.dataType)
                || (scheduledRefresh && refreshProgress?.currentType === status.dataType);
              const doneOk = !!rb && rb.success === true;
              const doneOkScheduled = scheduledRefresh
                && typeIdx >= 0
                && typeIdx < (refreshProgress?.current ?? 0);
              const doneFail = !!rb && rb.success === false;
              const refreshWindowActive =
                dataRefreshing &&
                refreshProgress?.requestedStartDate &&
                refreshProgress?.requestedEndDate;
              let cardModifier = '';
              if (running) cardModifier = ' refresh-running';
              else if (doneFail) cardModifier = ' refresh-done-error';
              else if (doneOk || doneOkScheduled) cardModifier = ' refresh-done-ok';

              return (
              <div
                key={status.dataType}
                className={`status-card ${status.recordCount > 0 ? 'cached' : 'empty'}${cardModifier}`}
              >
                <div className="status-card-name">{status.dataTypeName}</div>
                <div className="status-card-count">
                  {status.recordCount > 0 ? `${status.recordCount.toLocaleString()} records` : 'No data'}
                </div>
                {status.startDate && status.endDate && (
                  <>
                    <div className="status-card-section-label">In cache</div>
                    <div className="status-card-range">
                      {formatDateShort(status.startDate.split('T')[0])} – {formatDateShort(status.endDate.split('T')[0])}
                    </div>
                  </>
                )}
                {(refreshWindowActive || rb || (scheduledRefresh && (running || doneOkScheduled))) && (
                  <div className="status-card-this-run">
                    <div className="status-card-section-label">This refresh</div>
                    {refreshWindowActive && (
                      <div className="status-card-refresh-window" title="Request window selected in the panel">
                        Ask:{' '}
                        {formatRefreshRangePiece(refreshProgress.requestedStartDate)} —{' '}
                        {formatRefreshRangePiece(refreshProgress.requestedEndDate)}
                      </div>
                    )}
                    {running && (
                      <div className="status-card-run-state status-card-run-pulse">Working…</div>
                    )}
                    {(doneOk || doneOkScheduled) && !running && (
                      <>
                        {(rb?.actualStartDate || rb?.actualEndDate) && (
                          <div className="status-card-loaded-span" title="Min/max timestamps on rows inserted this run">
                            Loaded:{' '}
                            {formatRefreshRangePiece(rb.actualStartDate)} —{' '}
                            {formatRefreshRangePiece(rb.actualEndDate)}
                          </div>
                        )}
                        <div className="status-card-run-state ok">
                          Done
                          {typeof rb?.recordCount === 'number' ? ` · ${rb.recordCount.toLocaleString()} new rows` : ''}
                        </div>
                      </>
                    )}
                    {doneFail && (
                      <div className="status-card-run-state err" title={rb.error || ''}>
                        Failed: {truncate(rb.error)}
                      </div>
                    )}
                  </div>
                )}
                <div className="status-card-date">
                  {status.lastRefreshed ? `Updated: ${formatDateTime(status.lastRefreshed)}` : 'Never refreshed'}
                </div>
              </div>
              );
            })}
            <div className={`status-card facility-card ${facilityStatus.facilityCount > 0 ? 'cached' : 'empty'}${facilitiesRefreshing ? ' refresh-running' : ''}`}>
              <div className="status-card-name">Facilities</div>
              <div className="status-card-count">
                {facilityStatus.facilityCount > 0 ? `${facilityStatus.facilityCount} facilities` : 'No data'}
              </div>
              <div className="status-card-date">
                {facilityStatus.lastRefreshed ? `Updated: ${formatDateTime(facilityStatus.lastRefreshed)}` : 'Never refreshed'}
              </div>
              {salesforceSyncEnabled ? (
                <button
                  className="refresh-facilities-btn"
                  onClick={(e) => { e.stopPropagation(); onRefreshFacilities(); }}
                  disabled={dataRefreshing || facilitiesRefreshing}
                >
                  {facilitiesRefreshing ? 'Syncing...' : 'Sync Facilities'}
                </button>
              ) : (
                <div className="status-card-run-state" title="SALESFORCE_SYNC_ENABLED=false">
                  Salesforce sync paused — using cache
                </div>
              )}
            </div>
          </div>

          <div className="refresh-controls">
            <div className="refresh-control-row">
              <div className="type-selector">
                <label>Data Types:</label>
                <div className="type-actions">
                  <button className="link-btn" onClick={selectAllTypes}>All</button>
                  <button className="link-btn" onClick={selectNoneTypes}>None</button>
                </div>
                <div className="type-checkboxes">
                  {ALL_DATA_TYPES.map(dt => (
                    <label key={dt.id} className="type-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedTypes.has(dt.id)}
                        onChange={() => toggleType(dt.id)}
                      />
                      {dt.name}
                    </label>
                  ))}
                </div>
              </div>

              <div className="date-controls">
                <div className="date-control-group">
                  <label>Date Range:</label>
                  <select value={dateRange} onChange={(e) => setDateRange(e.target.value)} className="date-range-dropdown">
                    <option value="last_week">Last 7 Days</option>
                    <option value="last_month">Last 30 Days</option>
                    <option value="last_3_months">Last 3 Months</option>
                    <option value="last_6_months">Last 6 Months</option>
                    <option value="last_year">Last 12 Months</option>
                    <option value="last_18_months">Last 18 Months</option>
                    <option value="last_2_years">Last 2 Years</option>
                    <option value="custom">Custom Range</option>
                  </select>
                  {dateRange !== 'custom' && startDate && endDate && (
                    <span
                      className="date-range-hint"
                      title="The exact start and end dates this range resolves to. All presets are trailing windows ending today."
                    >
                      {formatDateShort(startDate)} &ndash; {formatDateShort(endDate)}
                    </span>
                  )}
                </div>

                {dateRange === 'custom' && (
                  <div className="custom-dates">
                    <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                    <span>to</span>
                    <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                  </div>
                )}

                <div className="date-control-group">
                  <label>Mode:</label>
                  <select
                    value={mode}
                    onChange={(e) => handleModeChange(e.target.value)}
                    className="date-range-dropdown"
                  >
                    <option value="smart">Smart Refresh (new data only)</option>
                    <option value="full-range">Full Refresh of Range and Types</option>
                    <option value="full-wipe">Full Refresh Wipe Everything</option>
                  </select>
                  <span className="date-range-hint">{modeHelp}</span>
                  {mode === 'full-wipe' && (
                    <label className="refresh-wipe-confirm">
                      <input
                        type="checkbox"
                        checked={wipeConfirmed}
                        onChange={(e) => setWipeConfirmed(e.target.checked)}
                      />
                      I understand this deletes all cached history for the selected types, not just this date range
                    </label>
                  )}
                </div>
              </div>
            </div>

            <div className="refresh-actions">
              <button
                className="refresh-btn primary"
                onClick={handleRefresh}
                disabled={dataRefreshing || selectedTypes.size === 0 || (mode === 'full-wipe' && !wipeConfirmed)}
              >
                {dataRefreshing ? 'Refreshing...' : 'Refresh Data'}
              </button>
              {dataRefreshing && (
                <button
                  className="refresh-btn danger"
                  onClick={onCancelRefresh}
                  title="Request cancellation between data types (current request may finish first)"
                >
                  Stop Refresh
                </button>
              )}
              <button className="refresh-btn secondary" onClick={onOpenPhiConfig}>
                PHI Settings
              </button>
            </div>

            {refreshProgress && (
              <div className="refresh-progress">
                <div className="progress-bar-track">
                  <div
                    className="progress-bar-fill"
                    style={{ width: `${refreshProgress.percent ?? 0}%` }}
                  />
                </div>
                <div className="progress-label">
                  {refreshProgress.currentType
                    ? `Refreshing ${ALL_DATA_TYPES.find(d => d.id === refreshProgress.currentType)?.name || refreshProgress.currentType}… (${refreshProgress.current + 1} of ${refreshProgress.total})`
                    : refreshProgress.percent >= 100
                      ? `Complete — ${refreshProgress.total} data type${refreshProgress.total !== 1 ? 's' : ''} processed`
                      : 'Starting…'}
                </div>
              </div>
            )}
          </div>

          {onImportSnapshot && (
            <CacheImportSection
              onImport={onImportSnapshot}
              disabled={dataRefreshing || facilitiesRefreshing}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default RefreshPanel;
